import { Box, Flex, Portal, Tooltip } from '@chakra-ui/react'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import EntityPreview from 'modules/EntityPreview'

import { useGlobalSearch } from 'contexts/GlobalSearch'

import { EntitySearchType, IResponseBase } from 'api/types'

import {
  EntityComponentPropsTypes,
  SearchEntryPropTypes,
} from 'interfaces/entity.interface'
import { IModel } from 'interfaces/model.interface'
import { ISingleNavigationPage } from 'interfaces/navigationPage.interface'

import { appsListUnion } from 'config/apps'

import resolvePath from 'utils/resolvePath'

import SearchEntityEntry from './SearchEntityEntry'
import { allAppsWithPagesT } from 'modules/AppsSelector/AppsGlobalSearch'
import { List as VirtualizedList, AutoSizer } from 'react-virtualized'
import 'react-virtualized/styles.css' // only needs to be imported once

type PropTypes = {
  data: EntitySearchType[]
  app?: appsListUnion
  page: ISingleNavigationPage<any, IModel<any>>
  searchString: string
  searchType?: string
  reportView?: boolean
  allAppsWithPages?: allAppsWithPagesT
}

const EntryAppsMapping = {
  app_science_macro: 'Biorisk',
  app_ida: 'IDA',
  app_rna_therapeutics: 'RNA',
  app_covid_treatments: 'Covid-19 Tx',
  app_rsv: 'RSV',
  app_influenza: 'Influenza',
  demo_vaccines: 'Covid-19',
  app_pad: 'PAD',
  app_pnh: 'PNH',
  app_covid_epidemiology: 'Covid-19 Epi',
  app_covid_diagnostics: 'C-19 Diagnostics',
  app_obesity_360: 'Obesity 360',
  'app_cardio_anti-thrombotics': 'Cardio',
  app_cardio_lipid: 'Cardio Lipid',
  app_covid_treatments_market_and_production: 'Covid-19 Tx Market',
  app_influenza_market_and_production: 'Influenza Market',
  app_supply_and_production: 'Covid-19 Commercial',
  app_esc2021: 'ESC 2021',
}
export const EntryAppsMapping2 = {
  app_science_macro: 'biorisk',
  app_ida: 'ida-360',
  app_rna_therapeutics: 'rna',
  app_covid_treatments: 'covid-19-therapeutics',
  app_rsv: 'rsv',
  app_influenza: 'influenza',
  demo_vaccines: 'covid-19-vaccines',
  app_pad: 'pad',
  app_pnh: 'pnh',
  app_covid_epidemiology: 'epidemiology',
  app_covid_diagnostics: 'covid-diagnostics',
  'app_cardio_anti-thrombotics': 'cardio-antithrombotics',
  app_cardio_lipid: 'cardio-lipid',
  app_covid_treatments_market_and_production:
    'covid-19-commercial-therapeutics',
  app_influenza_market_and_production: 'influenza-commercial',
  app_supply_and_production: 'covid-19-commercial-vaccines',
  app_esc2021: 'esc-2021',
}

type EntityRenderPropTypes = {
  entry: EntitySearchType
  allAppsWithPages?: allAppsWithPagesT
  page: ISingleNavigationPage<any, IModel<any>>
  entity: {
    component?: React.FC<React.PropsWithChildren<EntityComponentPropsTypes>>
    renderDetail?: (data: IResponseBase<any>) => React.ReactNode
    searchEntryComponent?: React.FC<
      React.PropsWithChildren<SearchEntryPropTypes>
    >
  }
  setOverlayShown: (shown: boolean) => void
  reportView?: boolean
  i: number
  searchType?: string
  searchString?: string
}

type EntryAppsT = keyof typeof EntryAppsMapping

const AppSection = ({ entryApps }: { entryApps: string[] | undefined }) => {
  if (!entryApps) return null

  const appsNames = entryApps.map(
    (app) => EntryAppsMapping[app as EntryAppsT] || app
  )
  const [displayApp, ...otherApps] = appsNames

  return (
    <Flex flexDir={'row'} gap='2px' my='4px' pos='relative'>
      <Box
        key={entryApps[0]}
        textAlign={'center'}
        bg='white'
        border={'1px solid'}
        borderColor='gray3'
        px='8px'
        borderRadius={'md'}
        fontSize={'sm'}
        w='fit-content'
        whiteSpace={'nowrap'}
      >
        {displayApp}
      </Box>
      {otherApps.length > 0 && (
        <Tooltip label={otherApps.join(', ')}>
          <Box
            cursor={'pointer'}
            textAlign={'center'}
            bg='white'
            border={'1px solid'}
            borderColor='gray3'
            px='4px'
            borderRadius={'md'}
            fontSize={'sm'}
          >
            +{otherApps.length}
          </Box>
        </Tooltip>
      )}
    </Flex>
  )
}

const EntityRender = ({
  entry,
  allAppsWithPages,
  page,
  entity,
  setOverlayShown,
  reportView,
  i,
  searchType,
  searchString,
}: EntityRenderPropTypes) => {
  const entryApps = entry.apps?.sort((a, b) => {
    // Ensure app_ida is always the first on the list
    if (a === 'app_ida') return -1
    if (b === 'app_ida') return 1
    return 1
  })
  const entryApp = (
    entryApps && entryApps[0] in EntryAppsMapping2
      ? EntryAppsMapping2[entryApps[0] as keyof typeof EntryAppsMapping2]
      : ''
  ) as appsListUnion

  const page2 = Object.values(allAppsWithPages?.[entryApp] ?? {}).find(
    (app) => app.model.endpoint === entry.endpoint
  )
  const selectedPage = page2 || page
  const entryProps: SearchEntryPropTypes = {
    data: entry,
    app: entryApp,
    page: selectedPage,
    entityId: entry.id,
  }

  const model = selectedPage.model.previewOverrideModel
    ? typeof selectedPage.model.previewOverrideModel === 'function'
      ? selectedPage.model.previewOverrideModel(selectedPage.model)
      : selectedPage.model.previewOverrideModel
    : selectedPage.model

  const link =
    selectedPage.path === null
      ? '#'
      : resolvePath([entryApp, selectedPage.path, encodeURIComponent(entry.id)])

  const entityEntry = React.createElement(
    entity?.searchEntryComponent || SearchEntityEntry,
    entryProps
  )
  // HACK to handle the case where displayText is an object
  if (
    typeof entry.displayText === 'object' &&
    (entry.displayText as any)?.title
  ) {
    entry.displayText = (entry.displayText as any).title
  }
  return (
    <Flex
      h='50px'
      maxH='50px'
      overflow={'hidden'}
      alignItems={'center'}
      key={i}
      onClick={() => setOverlayShown(false)}
      zIndex={reportView ? 9999 : 1}
      borderBottom={'1px solid'}
      borderColor={'gray3'}
    >
      <SectionEntry
        app={entryApp}
        entryApps={entryApps}
        link={link}
        model={model}
        entityEntry={entityEntry}
        entry={entry}
        reportView={reportView}
        searchType={searchType}
        searchString={searchString}
      />
    </Flex>
  )
}

export default function GlobalEntitySection({
  allAppsWithPages,
  page,
  data,
  reportView,
  searchString,
  searchType,
}: PropTypes) {
  const entity = { ...page.model.defaultEntity, ...page.entity }
  const { setOverlayShown } = useGlobalSearch()
  const [boxHeight, setBoxHeight] = useState<number | null>(null)
  const boxRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const updateBoxHeight = () => {
      const element = document.getElementById('global-search-left-section')
      if (element) {
        setBoxHeight(element.offsetHeight)
      }
    }

    updateBoxHeight()
    window.addEventListener('resize', updateBoxHeight)

    return () => {
      window.removeEventListener('resize', updateBoxHeight)
    }
  }, [])

  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    index: number
    key: string
    style: React.CSSProperties
  }) => {
    const entry = data[index]
    return (
      <div key={key} style={style}>
        <EntityRender
          entry={entry}
          allAppsWithPages={allAppsWithPages}
          page={page}
          entity={entity}
          setOverlayShown={setOverlayShown}
          reportView={reportView}
          i={index}
          searchType={searchType}
          searchString={searchString}
        />
      </div>
    )
  }

  return (
    <Box mb={8} w='100%' h={`${(boxHeight ?? 1000) - 400}px`}>
      <AutoSizer>
        {({ height, width }) => (
          <VirtualizedList
            width={width} // Adjust as needed
            height={height}
            rowCount={data.length}
            rowHeight={50} // Adjust row height as needed
            rowRenderer={rowRenderer}
          />
        )}
      </AutoSizer>
    </Box>
  )
}

type SectionEntryPropTypes = {
  model: IModel<any, any>
  link: string
  entry: EntitySearchType
  entityEntry: React.ReactNode
  reportView?: boolean
  app?: appsListUnion
  searchType?: string
  searchString?: string
  entryApps?: string[] | undefined
}

const SectionEntry = ({
  app,
  model,
  link,
  entry,
  entityEntry,
  reportView,
  searchType,
  searchString,
  entryApps,
}: SectionEntryPropTypes) => {
  const [isHovered, setIsHovered] = useState(false)

  const SelectedHoveredView = () => (
    <Box
      backgroundColor='white'
      zIndex={151}
      maxHeight='80vh'
      overflow='auto'
      p={0}
      {...(reportView
        ? {
            right: '20%',
            top: '0',
            transform: 'translate(100%,0)',
            maxW: '400px',
          }
        : { left: '35%', top: '50%', transform: 'translateY(-40%)' })}
      width='700px'
      position='absolute'
      boxShadow='rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.06) 0px 2px 4px'
    >
      <Box height='75vh' overflow='hidden'>
        {model.endpoint ? (
          <EntityPreview app={app} model={model} entityData={entry} />
        ) : (
          'No data Visible'
        )}
      </Box>
    </Box>
  )
  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      position={reportView ? 'unset' : 'relative'}
      w='full'
    >
      <Link
        to={`${link}?searchType=${searchType}&searchString=${searchString}`}
      >
        <Flex alignItems={'center'} justifyContent={'space-between'} w='full'>
          <Box
            id='test'
            display={'flex'}
            py={3}
            fontSize='14px'
            fontWeight={400}
            cursor='pointer'
            _hover={{
              bg: 'gray.50',
            }}
            overflow='hidden'
            noOfLines={2}
          >
            {entityEntry}
          </Box>
          <AppSection entryApps={entryApps} />
        </Flex>
      </Link>
      {isHovered &&
        (reportView ? (
          <SelectedHoveredView />
        ) : (
          <Portal>
            <SelectedHoveredView />
          </Portal>
        ))}
    </Box>
  )
}
